import { useFullRelease } from "@/common/hooks_useFullRelease";
import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

interface DerivedTableDebugCtx {
  selectedTableId: string | null;
  setSelectedTableId: (id: string | null) => void;
  selectedInstance: string | null;
  setSelectedInstance: (instance: string | null) => void;
}

const Ctx = createContext<DerivedTableDebugCtx | null>(null);

/**
 * Shares "which derived table / which entity instance is selected" between the toolbar
 * controls and the result grid, which are rendered as siblings inside GraphViz's tree
 * (GraphToolbar and GraphCore respectively) and have no other way to share this UI state.
 */
export const DerivedTableDebugProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const release = useFullRelease();
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);

  // A selection from a previously-viewed release shouldn't carry over - its table/instance ids
  // are meaningless (or worse, coincidentally valid) for a different release. Only reset on an
  // actual switch (tracked via ref), not on initial mount - resetting unconditionally on mount
  // would run after (and clobber) the toolbar's own first-selection effect, since child effects
  // fire before parent effects.
  const previousReleaseIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previousReleaseId = previousReleaseIdRef.current;
    previousReleaseIdRef.current = release?.id;
    if (previousReleaseId !== undefined && previousReleaseId !== release?.id) {
      setSelectedTableId(null);
      setSelectedInstance(null);
    }
  }, [release?.id]);

  return (
    <Ctx.Provider value={{ selectedTableId, setSelectedTableId, selectedInstance, setSelectedInstance }}>
      {children}
    </Ctx.Provider>
  );
};

export const useDerivedTableDebug = (): DerivedTableDebugCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDerivedTableDebug must be used within a DerivedTableDebugProvider");
  return ctx;
};
